/**
 * Operator-Admin API shared Zod schemas.
 *
 * Imported by:
 *   - `apps/api/src/routes/admin/*` — response-validation source of
 *     truth.
 *   - `apps/admin/src/lib/api.ts` + page-level types — derive TS
 *     interfaces for `useEffect`/`useState` payloads.
 *
 * Convention identical to `portal-schemas.ts`: wire keys are
 * `snake_case`, UI camelCases at the boundary.
 */
import { z } from 'zod';

/**
 * Operator role. Same vocabulary used in `apps/api/src/db/schema/users.ts`.
 * Currently only `super_admin` exists, but the schema is forward-ready
 * for finer-grained roles when the operator team grows beyond Daniel.
 */
export const operatorRoleSchema = z.enum(['super_admin']);
export type OperatorRole = z.infer<typeof operatorRoleSchema>;

/**
 * `GET /v1/auth/me` response (operator surface, served on
 * `control.orboto.io`).
 */
export const operatorUserPayloadSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  role: operatorRoleSchema,
});
export type OperatorUserPayload = z.infer<typeof operatorUserPayloadSchema>;

/**
 * Stable error-body shape returned by every `/v1/*` endpoint on the
 * operator surface. Same shape as `portalErrorResponseSchema` — kept
 * separate so admin-side and portal-side can evolve independently if
 * one needs additional fields (e.g. correlation_id) later.
 */
export const adminErrorResponseSchema = z.object({ error: z.string() });
export type AdminErrorResponse = z.infer<typeof adminErrorResponseSchema>;

/**
 * OCP-347 / OCP-567 - monitoring role of a fleet server, distinct from
 * `pool_tag` (placement). One source of truth for the API zod enums, the
 * admin pickers and the pool/role consistency rule.
 *
 *   tenant      runs customer tenant stacks behind the Coolify proxy
 *   storage     RustFS / Postgres data box (disk-critical, stricter bar)
 *   infra       LB / OCP host / other operator infrastructure
 *   ai-gateway  the managed-AI LiteLLM gateway host (OCP-567): monitored
 *               like infra, never tenant-placeable, plus the gateway
 *               capacity model (memory / cpu / throughput / upstream)
 *   ai-backend  the managed-AI MODEL host (OCP-622): the vLLM embedding
 *               backend the gateway forwards to. Agent-only (no Coolify,
 *               no SSH from OCP - LAN-only box), GPU probe in the beacon,
 *               the `backend` dimension of the capacity model
 *   control-plane  the host that runs OCP itself (control / account / www /
 *               docs / its Coolify) - OCP-807. Patched by the nightly run
 *               LAST and rebooted only inside the maintenance window via a
 *               delayed `shutdown -r +1` after the run is reported; never a
 *               placement target (pool_tag `control-plane`, and the picker
 *               refuses the role outright).
 *
 * Only `tenant` is tenant-serving: every other role skips the branded
 * maintenance container + Traefik catch-all (OCP-462) and is refused by
 * the placement picker through its non-tenant pool tag.
 */
export const SERVER_ROLES = ['tenant', 'storage', 'infra', 'ai-gateway', 'ai-backend', 'control-plane'] as const;
export const serverRoleSchema = z.enum(SERVER_ROLES);
export type ServerRole = z.infer<typeof serverRoleSchema>;

/** OCP-807 - the role (and the matching pool tag) of the host that runs OCP itself. */
export const CONTROL_PLANE_ROLE = 'control-plane';

/** OCP-807 - true for the host that runs OCP itself (patched last, delayed reboot, never placed). */
export function isControlPlaneRole(role: string | null | undefined): boolean {
  return (role ?? '').trim() === CONTROL_PLANE_ROLE;
}

/** True only for the role that fronts customer tenants. */
export function isTenantServingRole(role: string | null | undefined): boolean {
  return ((role ?? 'tenant').trim() || 'tenant') === 'tenant';
}
